import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { EyeOff, ExternalLink, Link2Off } from 'lucide-react';
import { avisoDoEmbutido, urlEmbutivel, type LinkExterno } from '@malgoos/shared';
import { Botao, Cartao, classesBotao } from '../componentes/base';
import { Vazio } from '../componentes/estados';
import { SubtituloPagina } from '../componentes/subtitulo-pagina';
import { TextoCortado } from '../componentes/texto-cortado';
import { useDiscreto } from '../componentes/sensivel';
import { useSessao } from '../nucleo/sessao';

/** Caminho da página do link externo embutido (a tela inicial e o gerador de caminhos apontam para cá). */
export const caminhoExterno = (codigo: string) => `/externo/${codigo}`;

/**
 * Permissões que o quadro dá ao destino (FUN 2026-10-14). Sem `allow-top-navigation`: o destino nunca troca a página do
 * MalgoOS. `allow-same-origin` mantém a origem do próprio destino (o cofre guarda a sessão dele), nunca a do MalgoOS.
 */
export const SANDBOX_EMBUTIDO = 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads';
/** Copiar e colar e a chave de segurança (WebAuthn) do login em duas etapas do destino. */
export const ALLOW_EMBUTIDO = 'clipboard-read; clipboard-write; publickey-credentials-get *';

/**
 * "Celular" (r58 robustez M1, r59 spec B2): largura abaixo de 768 px, ou toque (`pointer: coarse`) com o menor lado abaixo de
 * 600 px. O celular deitado passa de 768 px de largura, mas fica com menos de 600 de altura; o tablet deitado tem menos de 768
 * de altura útil (barras do navegador), mas 600 ou mais, e segue tablet: abre o quadro direto. Janela estreita no computador
 * segue a largura. (Com toque e largura abaixo de 600, a primeira condição já vale.)
 */
export const CONSULTA_CELULAR = '(max-width: 767px), (pointer: coarse) and (max-height: 599px)';

export function useCelular(): boolean {
  const [celular, setCelular] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(CONSULTA_CELULAR).matches);
  useEffect(() => {
    if (!window.matchMedia) return;
    const m = window.matchMedia(CONSULTA_CELULAR);
    const mudou = () => setCelular(m.matches);
    mudou();
    m.addEventListener('change', mudou);
    return () => m.removeEventListener('change', mudou);
  }, []);
  return celular;
}

/**
 * FUN 2026-10-14 (cofre embutido): link externo marcado "Abrir dentro do MalgoOS" abre aqui, com o cabeçalho e o menu do
 * sistema e o destino num quadro ocupando o conteúdo. O MalgoOS só emoldura: nunca lê nem guarda o que acontece dentro (o
 * cofre continua sendo o Vaultwarden, com o login dele). Os links vêm da sessão, já filtrados por "Quem vê" (papéis) no
 * servidor; link fora da lista dá "não encontrado". Só host da lista fechada (`urlEmbutivel`) vira quadro; o resto oferece
 * a nova aba. No celular a nova aba é o padrão (o quadro fica a um toque); com o modo discreto ligado, o quadro some.
 * Review r58 robustez M1: depois de montado, o quadro fica montado (girar o celular ou estreitar a janela não recarrega o
 * cofre, que perderia o desbloqueio e o que estava sendo digitado).
 */
export function PaginaExterna() {
  const { codigo } = useParams();
  const s = useSessao();
  const desktop = !useCelular();
  const { ativo: discreto } = useDiscreto();
  const link = (s.subsistemas?.workspace.links ?? []).find((l) => l.codigo === codigo);
  return <TelaExterna key={codigo} link={link} desktop={desktop} discreto={discreto} />;
}

/**
 * A tela em si (sem sessão nem rota: testada por `renderToString` em `tests/cofre-embutido.test.ts`). `desktop` quer dizer
 * "não é celular" (`useCelular`): o quadro abre direto.
 */
export function TelaExterna({ link, desktop, discreto }: { link: LinkExterno | undefined; desktop: boolean; discreto: boolean }) {
  const { t } = useTranslation();
  // Trava do quadro (r58 robustez M1): liga no "Abrir aqui mesmo assim" ou na primeira vez que o quadro aparece, e não volta.
  const [fixo, setFixo] = useState(false);
  const refQuadro = useRef<HTMLDivElement>(null);
  const refIframe = useRef<HTMLIFrameElement>(null);
  const refCapa = useRef<HTMLDivElement>(null);
  // Review r60 robustez B1 = visual B1: a capa tinha o foco quando o discreto desligou (a capa sai da página com ele).
  const capaComFoco = useRef(false);
  const idCapa = useId();
  const embutivel = !!link?.embutir && urlEmbutivel(link.url);
  const quadro = embutivel && (desktop || fixo);
  if (quadro && !fixo) setFixo(true);
  // Review r58 visual B2: a altura do quadro sai do topo medido dele (a 1024 o cabeçalho quebra em duas linhas e o aviso em
  // três), não de um desconto fixo: a página não ganha rolagem além da do próprio destino.
  useEffect(() => {
    const el = refQuadro.current;
    if (!quadro || !el) return;
    const medir = () => el.style.setProperty('--quadro-topo', `${Math.ceil(el.getBoundingClientRect().top + window.scrollY + (el.closest('[data-rolagem-conteudo]')?.scrollTop ?? 0))}px`);
    medir();
    window.addEventListener('resize', medir);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    if (el.parentElement) ro?.observe(el.parentElement);
    return () => {
      window.removeEventListener('resize', medir);
      ro?.disconnect();
    };
  }, [quadro]);
  // Review r59 robustez M1: ligar o discreto com o foco no quadro tira o foco dele (`inert` não tira o foco de um documento de
  // outra origem): as teclas não seguem entrando no cofre escondido. O foco vai para a capa, que diz por que o quadro sumiu.
  // Efeito de layout (r61): roda antes de o navegador tirar o foco do quadro que acabou de sair do layout (`hidden`).
  useLayoutEffect(() => {
    const f = refIframe.current;
    if (!discreto) {
      // Review r60 robustez B1 = visual B1: desligar (atalho, olho ou outra aba) com o foco na capa devolve o foco ao quadro,
      // que voltou no lugar dela; sem isso o foco caía no body e o Tab seguinte saía da página.
      const voltar = capaComFoco.current;
      capaComFoco.current = false;
      const ativo = document.activeElement;
      if (voltar && f && (!ativo || ativo === document.body)) f.focus({ preventScroll: true });
      return;
    }
    // Foco que sai da capa para outro lugar (Tab, clique num controle) não volta ao quadro no desligar.
    const fora = (e: FocusEvent) => {
      if (e.target !== refCapa.current) capaComFoco.current = false;
    };
    document.addEventListener('focusin', fora);
    const tirar = () => {
      if (!f || document.activeElement !== f) return;
      f.blur();
      refCapa.current?.focus({ preventScroll: true });
    };
    tirar();
    // Review r61 segurança B2: o destino escondido tentava tomar o foco de volta (`campo.focus()` em laço ou senha mestra em
    // autofocus) e as teclas da pessoa entravam no cofre. A causa sai no quadro (`hidden`, abaixo: fora do layout, nada nele
    // recebe foco); a guarda síncrona no `blur` da janela fica como segunda barreira.
    window.addEventListener('blur', tirar);
    return () => {
      document.removeEventListener('focusin', fora);
      window.removeEventListener('blur', tirar);
    };
  }, [discreto, quadro]);
  if (!link) {
    return (
      <Cartao>
        <Vazio titulo={t('externo.naoEncontrado')} texto={t('externo.naoEncontradoTexto')} icone={<Link2Off className="size-5" aria-hidden />} />
      </Cartao>
    );
  }
  const aviso = avisoDoEmbutido(link.url);
  const novaAba = (primario: boolean) => (
    <a href={link.url} target="_blank" rel="noopener noreferrer" data-abrir-nova-aba="" className={classesBotao(primario ? 'primario' : 'secundario')}>
      <ExternalLink className="size-4" aria-hidden />
      {t('externo.novaAba')}
    </a>
  );
  return (
    <div className="flex min-w-0 flex-col gap-4" data-externo={link.codigo}>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="kicker">{t('externo.kicker')}</p>
          <TextoCortado como="h1" className="mt-1 text-2xl font-bold tracking-tight">
            {link.nome}
          </TextoCortado>
          <SubtituloPagina caminho={caminhoExterno(link.codigo)} />
          <p className="mt-0.5 text-xs break-all text-tinta-3">{new URL(link.url).hostname}</p>
        </div>
        {quadro && novaAba(false)}
      </header>
      {quadro ? (
        <>
          <p role="note" data-aviso-externo="" className="text-xs text-tinta-3">
            {aviso ? `${t(`externo.avisos.${aviso}`)} ` : ''}
            {t('externo.avisoNovaAba')} {t('externo.avisoDiscreto')}
          </p>
          <div ref={refQuadro} className="relative min-w-0 overflow-hidden rounded-xl border border-borda bg-sup-1">
            <iframe
              ref={refIframe}
              src={link.url}
              title={t('externo.quadro', { nome: link.nome })}
              data-quadro-externo=""
              sandbox={SANDBOX_EMBUTIDO}
              allow={ALLOW_EMBUTIDO}
              referrerPolicy="no-referrer"
              inert={discreto}
              aria-hidden={discreto || undefined}
              // Review r61 segurança B2: com o discreto, o quadro sai do layout (`hidden`, sem desmontar nem recarregar, r58): o
              // destino escondido não consegue tomar o foco de volta (num quadro só `invisible` ele conseguia, e as teclas da
              // pessoa entravam no cofre). O lugar fica com uma moldura vazia da mesma altura, sob a capa.
              // Review r62 robustez B1 (decisão): o `hidden` faz o destino perder a rolagem do documento se ele mexer no layout
              // escondido; `visibility:hidden` e `content-visibility:hidden`, mesmo com `inert` e a guarda no `blur`, guardam a
              // rolagem mas deixam as teclas entrar no cofre (`scripts/provas/lentes/r62-esconder-quadro.ts`). Segurança acima da rolagem.
              className={`altura-quadro-externo w-full border-0 ${discreto ? 'hidden' : 'block'}`}
            />
            {discreto && <div aria-hidden className="altura-quadro-externo block w-full" data-quadro-reserva="" />}
            {discreto && (
              <div
                ref={refCapa}
                tabIndex={-1}
                // Review r60 visual B2: com o foco, o leitor de tela lê o nome ("Modo discreto ligado") e o porquê.
                role="region"
                aria-labelledby={`${idCapa}-titulo`}
                aria-describedby={`${idCapa}-texto`}
                onFocus={(e) => {
                  if (e.target === e.currentTarget) capaComFoco.current = true;
                }}
                className="absolute inset-0 flex items-center justify-center focus:outline-none"
                data-externo-discreto=""
              >
                <Vazio
                  titulo={t('externo.discretoTitulo')}
                  texto={t('externo.discretoTexto', { nome: link.nome })}
                  icone={<EyeOff className="size-5" aria-hidden />}
                  idTitulo={`${idCapa}-titulo`}
                  idTexto={`${idCapa}-texto`}
                />
              </div>
            )}
          </div>
        </>
      ) : (
        <Cartao>
          <Vazio
            titulo={embutivel ? t('externo.celularTitulo') : t('externo.soNovaAba')}
            texto={embutivel ? t('externo.celularTexto', { nome: link.nome }) : t('externo.soNovaAbaTexto')}
            icone={<ExternalLink className="size-5" aria-hidden />}
            acao={
              <div className="flex flex-wrap items-center justify-center gap-2">
                {novaAba(true)}
                {embutivel && (
                  <Botao variante="fantasma" onClick={() => setFixo(true)} data-abrir-aqui="">
                    {t('externo.abrirAqui')}
                  </Botao>
                )}
              </div>
            }
          />
        </Cartao>
      )}
    </div>
  );
}
